// Register.jsx - Location Restricted to Hyderabad Only
import React, { useState, useRef, useEffect } from 'react';
import { registerUser, sendOtp, sendPhoneOtp, verifyPhoneOtp } from '../api';
import { showError } from './AlertModal';
import {
  EnvelopeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export default function Register({ onClose, onSwitchToLogin, onRegisterSuccess }) {
  const [userType, setUserType] = useState('b2c');
  const [step, setStep] = useState(1); // 1=type, 2=form, 3=phone OTP, 4=email OTP
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    businessName: '',
    gstNumber: '',
    location: {
      address: '',
      city: 'Hyderabad',
      pincode: '',
    }
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [gstError, setGstError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);

  // OTP states (email)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [pendingFormData, setPendingFormData] = useState(null);
  const otpInputRefs = useRef([]);
  const timerRef = useRef(null);

  // Phone OTP states
  const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', '']);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState('');
  const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const phoneOtpRefs = useRef([]);
  const phoneTimerRef = useRef(null);

  // Location state
  const [locationMode, setLocationMode] = useState('detect'); // 'detect' | 'manual'
  const [locationSelected, setLocationSelected] = useState(false);

  // Hyderabad pincodes list (500001–500100)
  const HYDERABAD_PINCODES = [
    '500001', '500002', '500003', '500004', '500005', '500006', '500007', '500008', '500009', '500010',
    '500011', '500012', '500013', '500014', '500015', '500016', '500017', '500018', '500019', '500020',
    '500021', '500022', '500023', '500024', '500025', '500026', '500027', '500028', '500029', '500030',
    '500031', '500032', '500033', '500034', '500035', '500036', '500037', '500038', '500039', '500040',
    '500041', '500042', '500043', '500044', '500045', '500046', '500047', '500048', '500049', '500050',
    '500051', '500052', '500053', '500054', '500055', '500056', '500057', '500058', '500059', '500060',
    '500061', '500062', '500063', '500064', '500065', '500066', '500067', '500068', '500069', '500070',
    '500071', '500072', '500073', '500074', '500075', '500076', '500077', '500078', '500079', '500080',
    '500081', '500082', '500083', '500084', '500085', '500086', '500087', '500088', '500089', '500090',
    '500091', '500092', '500093', '500094', '500095', '500096', '500097', '500098', '500099', '500100'
  ];

  const isValidHyderabadPincode = (pincode) => HYDERABAD_PINCODES.includes(pincode);

  // ── Location detection ───────────────────────────────────
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detectError, setDetectError] = useState('');

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      setDetectError('Geolocation not supported. Please enter manually.');
      setLocationMode('manual');
      return;
    }
    setDetectingLocation(true);
    setDetectError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          const addr = data.address || {};
          const road = addr.road || addr.footway || addr.pedestrian || '';
          const suburb = addr.suburb || addr.neighbourhood || addr.quarter || '';
          const pincode = (addr.postcode || '').replace(/\D/g, '').slice(0, 6);
          const address = [road, suburb].filter(Boolean).join(', ') || suburb || road;

          if (!pincode || !isValidHyderabadPincode(pincode)) {
            setDetectError('⚠️ Location detected but appears outside Hyderabad. Please enter manually.');
            setLocationMode('manual');
          } else {
            setFormData(prev => ({
              ...prev,
              location: { address, city: 'Hyderabad', pincode }
            }));
            setLocationSelected(true);
            setDetectError('');
            if (errors.location) setErrors(prev => ({ ...prev, location: '' }));
          }
        } catch {
          setDetectError('Could not fetch address. Please enter manually.');
          setLocationMode('manual');
        }
        setDetectingLocation(false);
      },
      (err) => {
        const msgs = {
          1: 'Location access denied. Please enter manually.',
          2: 'Location unavailable. Please enter manually.',
          3: 'Location request timed out. Please enter manually.',
        };
        setDetectError(msgs[err.code] || 'Unable to detect location.');
        setLocationMode('manual');
        setDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;

    if (name === 'pincode') {
      // Only allow digits, max 6
      const pincodeValue = value.replace(/\D/g, '').slice(0, 6);
      // Show warning only when 6 digits entered and not a valid Hyderabad code
      if (pincodeValue.length === 6 && !isValidHyderabadPincode(pincodeValue)) {
        setLocationError('⚠️ This pincode may not be in Hyderabad (500001–500100). Please verify.');
      } else {
        setLocationError('');
      }
      setFormData(prev => ({ ...prev, location: { ...prev.location, pincode: pincodeValue } }));
    } else {
      setFormData(prev => ({ ...prev, location: { ...prev.location, [name]: value } }));
    }

    if (errors.location) setErrors(prev => ({ ...prev, location: '' }));
  };

  const validateGST = (gstNumber) => {
    if (!gstNumber) return true;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber);
  };

  const handleGSTChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 15) {
      handleInputChange({ target: { name: 'gstNumber', value } });
      if (value.length === 15) {
        setGstError(validateGST(value) ? '' : 'Invalid GST format');
      } else if (value.length > 0) {
        setGstError('GST number must be 15 characters');
      } else {
        setGstError('');
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim())   newErrors.name  = 'Name is required';
    if (!formData.email.trim())  newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.phone.trim())  newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone number must be 10 digits';

    if (!formData.password)      newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';

    if (userType === 'b2b' && !formData.businessName.trim())
      newErrors.businessName = 'Business name is required';

    // Location: must have address (via search selection OR manual entry)
    if (!formData.location.address.trim()) {
      newErrors.location = 'Please search and select your Hyderabad area, or enter manually';
    } else if (!formData.location.pincode) {
      newErrors.location = 'Pincode is required — please enter it manually if not auto-filled';
    } else if (!isValidHyderabadPincode(formData.location.pincode)) {
      newErrors.location = 'Please enter a valid Hyderabad pincode (500001–500100)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Build form payload
    const payload = {
      full_name:     formData.name,
      email:         formData.email,
      phone:         formData.phone,
      password:      formData.password,
      user_type:     userType,
      business_name: formData.businessName || '',
      gst_number:    formData.gstNumber    || '',
      location: {
        address: formData.location.address,
        city:    'Hyderabad',
        pincode: formData.location.pincode,
      },
    };

    // Step 1: Send phone OTP first
    setLoading(true);
    try {
      const data = await sendPhoneOtp({ phone: formData.phone });
      if (data.success) {
        setPendingFormData(payload);
        setPhoneOtp(['', '', '', '', '', '']);
        setPhoneOtpError('');
        startPhoneOtpTimer(60);
        setStep(3); // go to phone OTP step
        setTimeout(() => phoneOtpRefs.current[0]?.focus(), 100);
      } else {
        showError(data.message || 'Failed to send SMS OTP. Please try again.');
      }
    } catch (err) {
      console.error('Send phone OTP error:', err);
      showError(err?.message || 'Unable to connect to server. Make sure the PHP backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const startPhoneOtpTimer = (seconds) => {
    setPhoneOtpTimer(seconds);
    if (phoneTimerRef.current) clearInterval(phoneTimerRef.current);
    phoneTimerRef.current = setInterval(() => {
      setPhoneOtpTimer(prev => {
        if (prev <= 1) { clearInterval(phoneTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePhoneOtpChange = (idx, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const newOtp = [...phoneOtp];
    newOtp[idx] = digit;
    setPhoneOtp(newOtp);
    setPhoneOtpError('');
    if (digit && idx < 5) phoneOtpRefs.current[idx + 1]?.focus();
  };

  const handlePhoneOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !phoneOtp[idx] && idx > 0)
      phoneOtpRefs.current[idx - 1]?.focus();
  };

  const handlePhoneOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setPhoneOtp(pasted.split(''));
      phoneOtpRefs.current[5]?.focus();
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const otpStr = phoneOtp.join('');
    if (otpStr.length !== 6) { setPhoneOtpError('Please enter all 6 digits.'); return; }

    setPhoneOtpLoading(true);
    setPhoneOtpError('');
    try {
      const data = await verifyPhoneOtp({ phone: formData.phone, otp: otpStr });
      if (data.success) {
        setPhoneVerified(true);
        if (phoneTimerRef.current) clearInterval(phoneTimerRef.current);
        // Now send email OTP
        const emailData = await sendOtp(pendingFormData);
        if (emailData.success) {
          setOtpSent(true);
          setOtp(['', '', '', '', '', '']);
          setOtpError('');
          startOtpTimer(60);
          setStep(4); // go to email OTP step
          setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
        } else {
          showError(emailData.message || 'Phone verified but failed to send email OTP.');
        }
      } else {
        setPhoneOtpError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch {
      setPhoneOtpError('Server error. Please try again.');
    } finally {
      setPhoneOtpLoading(false);
    }
  };

  const handleResendPhoneOtp = async () => {
    if (!pendingFormData || phoneOtpTimer > 0) return;
    setPhoneOtpLoading(true);
    try {
      const data = await sendPhoneOtp({ phone: formData.phone });
      if (data.success) {
        setPhoneOtp(['', '', '', '', '', '']);
        setPhoneOtpError('');
        startPhoneOtpTimer(60);
        phoneOtpRefs.current[0]?.focus();
      } else {
        setPhoneOtpError('Failed to resend OTP.');
      }
    } catch { setPhoneOtpError('Server error.'); }
    finally { setPhoneOtpLoading(false); }
  };

  const startOtpTimer = (seconds) => {
    setOtpTimer(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (idx, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[idx] = digit;
    setOtp(newOtp);
    setOtpError('');
    if (digit && idx < 5) otpInputRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0)
      otpInputRefs.current[idx - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpInputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpStr = otp.join('');
    if (otpStr.length !== 6) { setOtpError('Please enter all 6 digits.'); return; }

    setOtpLoading(true);
    setOtpError('');
    try {
      const data = await registerUser({ ...pendingFormData, otp: otpStr });
      if (data.success) {
        // Do not auto-login after registration; force explicit sign-in.
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        localStorage.setItem('userLocation', JSON.stringify(pendingFormData.location));
        window.dispatchEvent(new Event('location-updated'));
        if (onRegisterSuccess) onRegisterSuccess(data.user);
        setSuccessInfo({ name: formData.name, email: formData.email, userType });
      } else {
        setOtpError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError('Server error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingFormData || otpTimer > 0) return;
    setOtpLoading(true);
    try {
      const data = await sendOtp(pendingFormData);
      if (data.success) {
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        startOtpTimer(60);
        otpInputRefs.current[0]?.focus();
      }
      else setOtpError(data.message || 'Failed to resend OTP.');
    } catch { setOtpError('Server error.'); }
    finally { setOtpLoading(false); }
  };

  const goBackToTypeSelection = () => {
    setStep(1);
    setFormData({
      name: '', email: '', phone: '', password: '',
      businessName: '', gstNumber: '',
      location: { address: '', city: 'Hyderabad', pincode: '' }
    });
    setErrors({});
    setLocationError('');
    setLocationSelected(false);
    setLocationMode('detect');
    setDetectError('');
    if (timerRef.current) clearInterval(timerRef.current);
    setOtpTimer(0);
    setOtpSent(false);
  };

  // Add CSS animations to document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-pulse {
        animation: pulse 2s infinite;
      }
      .animate-fadeIn {
        animation: fadeIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Styles ────────────────────────────────────────────────
  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '8px',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'all 0.2s ease'
  });

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem',
  };

  const errorText = (msg) =>
    msg ? <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{msg}</p> : null;

  // ── Success Screen ────────────────────────────────────────
  if (successInfo) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}>
        <div className="animate-fadeIn" style={{
          background: 'white', borderRadius: '16px',
          maxWidth: '400px', width: '92%',
          padding: '2rem 1.75rem',
          textAlign: 'center',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
        }}>
          {/* Check icon */}
          <div style={{
            width: '56px', height: '56px',
            background: '#dcfce7',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1rem',
          }}>
            <CheckCircleIcon style={{ width: '28px', height: '28px', color: '#16a34a' }} />
          </div>

          <h2 style={{ color: '#111827', margin: '0 0 0.35rem', fontSize: '1.2rem', fontWeight: 700 }}>
            Registration Successful
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 1.25rem', lineHeight: 1.5 }}>
            Welcome, <strong style={{ color: '#111827' }}>{successInfo.name}</strong>! Your{' '}
            {successInfo.userType === 'b2b' ? 'B2B business' : 'customer'} account is ready.
          </p>

          {/* Email notice */}
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: '8px', padding: '0.75rem 1rem',
            marginBottom: '1.25rem', textAlign: 'left',
            display: 'flex', gap: '10px', alignItems: 'flex-start'
          }}>
            <EnvelopeIcon style={{ width: '16px', height: '16px', color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#15803d', fontWeight: 600 }}>
                Confirmation email sent
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#374151' }}>
                Sent to <strong>{successInfo.email}</strong>
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '0.73rem', color: '#9ca3af' }}>
                Check spam if you don't see it
              </p>
            </div>
          </div>

          <button
            onClick={onSwitchToLogin}
            style={{
              width: '100%',
              padding: '0.65rem 1rem',
              background: 'linear-gradient(135deg, #16a34a, #15803d)',
              color: 'white', border: 'none', borderRadius: '8px',
              fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(22,163,74,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <ShieldCheckIcon style={{ width: '16px', height: '16px' }} />
            Sign In to Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Main Registration Modal ───────────────────────────────
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }} onClick={onClose}>
      <div className="animate-fadeIn km-register-modal" style={{
        background: 'white', borderRadius: '16px',
        maxWidth: '640px', width: '92%',
        maxHeight: '90vh', overflowY: 'auto',
        padding: 'clamp(1.1rem, 3.6vw, 2rem)', position: 'relative'
      }} onClick={e => e.stopPropagation()}>

        <button onClick={onClose} className="km-close-btn" style={{
          position: 'absolute', top: '1rem', right: '1rem',
          background: 'none', border: 'none',
          fontSize: '1.5rem', cursor: 'pointer', color: '#666',
          transition: 'color 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#000'}
        onMouseLeave={e => e.currentTarget.style.color = '#666'}>
          ×
        </button>

        <style>{`
          .km-register-modal {
            box-shadow: 0 18px 60px rgba(0,0,0,0.18);
          }
          .km-close-btn {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .km-close-btn:hover {
            background: #f3f4f6;
          }
          .km-register-form {
            display: flex;
            flex-direction: column;
            gap: 14px;
          }
          .km-register-form > div {
            margin-bottom: 0 !important;
          }
          .km-register-form input,
          .km-register-form textarea {
            line-height: 1.25;
          }
          .km-register-form input:focus,
          .km-register-form textarea:focus {
            border-color: #16a34a !important;
            box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15);
          }
          .km-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .km-span-2 {
            grid-column: 1 / -1;
          }
          @media (min-width: 560px) {
            .km-register-modal {
              padding: 24px 28px;
            }
            .km-register-form {
              gap: 16px;
            }
          }
          @media (min-width: 760px) {
            .km-grid {
              grid-template-columns: 1fr 1fr;
              gap: 16px 18px;
              align-items: start;
            }
          }
        `}</style>

        {/* ── Step 1: Choose account type ── */}
        {step === 1 ? (
          <>
            <h2 style={{ marginBottom: '0.5rem', color: '#1f2937', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>Create Your Account</h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Choose the type of account that suits you
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              <button
                onClick={() => handleUserTypeSelect('b2c')}
                style={{
                  padding: '1.5rem', border: '2px solid #e5e7eb',
                  borderRadius: '12px', background: 'white',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🛍️</div>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem', color: '#1f2937' }}>B2C Customer</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Shop as an individual customer</div>
              </button>

              <button
                onClick={() => handleUserTypeSelect('b2b')}
                style={{
                  padding: '1.5rem', border: '2px solid #e5e7eb',
                  borderRadius: '12px', background: 'white',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏪</div>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem', color: '#1f2937' }}>B2B Business</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Register as a business for bulk orders</div>
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Back button + heading */}
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={goBackToTypeSelection}
                style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
              >
                ← Back
              </button>
              <span style={{
                background: userType === 'b2b' ? '#eff6ff' : '#f0fdf4',
                color: userType === 'b2b' ? '#1d4ed8' : '#16a34a',
                padding: '2px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600
              }}>
                {userType === 'b2b' ? '🏪 B2B Business' : '🛍️ B2C Customer'}
              </span>
            </div>

            <h2 style={{ marginBottom: '1.5rem', color: '#1f2937', fontSize: 'clamp(1.1rem, 5vw, 1.3rem)' }}>
              {userType === 'b2c' ? 'Create Customer Account' : 'Register Your Business'}
            </h2>

            <form onSubmit={handleSubmit} className="km-register-form">

              {/* ── Step 2: Form fields ── */}
              {step === 2 && (<>
              <div className="km-grid">
              {/* Full Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text" name="name" value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  style={inputStyle(errors.name)}
                />
                {errorText(errors.name)}
              </div>

              {/* Email */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email" name="email" value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  style={inputStyle(errors.email)}
                />
                {errorText(errors.email)}
              </div>

              {/* Phone */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Phone Number *</label>
                <input
                  type="tel" name="phone" value={formData.phone}
                  onChange={handleInputChange}
                  maxLength="10"
                  placeholder="10-digit mobile number"
                  style={inputStyle(errors.phone)}
                />
                {errorText(errors.phone)}
              </div>

              {/* B2B extra fields */}
              {userType === 'b2b' && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>Business Name *</label>
                    <input
                      type="text" name="businessName" value={formData.businessName}
                      onChange={handleInputChange}
                      placeholder="Registered business name"
                      style={inputStyle(errors.businessName)}
                    />
                    {errorText(errors.businessName)}
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>
                      GST Number <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>(Optional)</span>
                    </label>
                    <input
                      type="text" name="gstNumber" value={formData.gstNumber || ''}
                      onChange={handleGSTChange}
                      placeholder="e.g. 36AAAAA0000A1Z5"
                      style={inputStyle(gstError)}
                    />
                    {errorText(gstError)}
                  </div>
                </>
              )}

              {/* Password */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Password *</label>
                <input
                  type="password" name="password" value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 6 characters"
                  style={inputStyle(errors.password)}
                />
                {errorText(errors.password)}
              </div>
              </div>

              {/* ── Location Section ── */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>
                Address <span style={{ color: '#ef4444' }}>*</span>
                </label>

             

                {/* Detect button — shown when not yet detected and not in manual mode */}
                {!locationSelected && locationMode !== 'manual' && (
                  <button
                    type="button"
                    onClick={detectLocation}
                    disabled={detectingLocation}
                    style={{
                      width: '100%', padding: '0.7rem',
                      background: detectingLocation ? '#e5e7eb' : '#fff',
                      border: '1.5px solid #d1d5db', borderRadius: '8px',
                      color: detectingLocation ? '#9ca3af' : '#374151',
                      fontWeight: 600, fontSize: '0.875rem', cursor: detectingLocation ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      marginBottom: '8px', transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { if (!detectingLocation) { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.color = '#16a34a'; } }}
                    onMouseLeave={e => { if (!detectingLocation) { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; } }}
                  >
                    {detectingLocation ? (
                      <>
                        <ArrowPathIcon style={{ width: '16px', height: '16px', animation: 'spin 0.7s linear infinite' }} />
                        Detecting location...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                          <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                        </svg>
                        Detect My Location
                      </>
                    )}
                  </button>
                )}

                {/* Detected address preview */}
                {locationSelected && locationMode !== 'manual' && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 600, color: '#15803d' }}>✓ Location detected</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#374151' }}>{formData.location.address}, Hyderabad – {formData.location.pincode}</p>
                    </div>
                    <button type="button" onClick={() => { setLocationSelected(false); setFormData(prev => ({ ...prev, location: { address: '', city: 'Hyderabad', pincode: '' } })); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1rem', padding: 0, flexShrink: 0 }}>✕</button>
                  </div>
                )}

                {/* Error from detection */}
                {detectError && (
                  <p style={{ color: '#dc2626', fontSize: '0.75rem', marginBottom: '8px' }}>{detectError}</p>
                )}

                {/* Toggle manual */}
                <button
                  type="button"
                  onClick={() => { setLocationMode(locationMode === 'manual' ? 'search' : 'manual'); setDetectError(''); }}
                  style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, padding: '0 0 8px', textDecoration: 'underline' }}
                >
                  {locationMode === 'manual' ? '📍 Use location detection instead' : '✏️ Enter address manually'}
                </button>

                {/* Manual entry fields */}
                {locationMode === 'manual' && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', background: '#f9fafb' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>House No. / Street / Area *</label>
                      <textarea
                        name="address" value={formData.location.address} onChange={handleLocationChange}
                        rows="2" placeholder="e.g. Flat 201, Rajiv Nagar, Banjara Hills"
                        style={{ width: '100%', padding: '0.65rem 0.75rem', border: `1px solid ${errors.location ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', fontSize: '0.8rem', resize: 'none', boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>City</label>
                        <input type="text" value="Hyderabad" readOnly style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', background: '#f3f4f6', color: '#6b7280', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Pincode *</label>
                        <input type="text" name="pincode" value={formData.location.pincode} onChange={handleLocationChange} placeholder="500001–500100" maxLength="6"
                          style={{ width: '100%', padding: '0.65rem 0.75rem', border: `1px solid ${locationError || errors.location ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', fontSize: '0.8rem', boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                  </div>
                )}

                {(locationError || errors.location) && (
                  <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{locationError || errors.location}</p>
                )}
              </div>

              {/* Submit button — step 2 only */}
              <button type="submit" disabled={loading}
                style={{ width: '100%', padding: '0.8rem', background: loading ? '#9ca3af' : 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: loading ? 'none' : '0 4px 12px rgba(22,163,74,0.3)' }}>
                {loading ? (
                  <><ArrowPathIcon style={{ width: '18px', height: '18px', animation: 'spin 0.6s linear infinite' }} />Sending OTP...</>
                ) : (
                  <><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>Send Mobile OTP</>
                )}
              </button>

              <p style={{ textAlign: 'center', fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                Already have an account?{' '}
                <button type="button" onClick={onSwitchToLogin} style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontWeight: 600 }}>Sign In</button>
              </p>
              </>)}

              {/* ── Phone OTP Step ── */}
              {step === 3 && (
                <div style={{ padding: '1rem 0 0.5rem' }}>
                  <style>{`
                    .km-otp-row { display: flex; gap: 8px; justify-content: center; margin: 16px 0; }
                    .km-otp-box { width: 42px; height: 42px; text-align: center; font-size: 1.1rem; font-weight: 700; font-family: monospace; border: 1.5px solid #d1d5db; border-radius: 8px; outline: none; background: #f9fafb; color: #111827; transition: all 0.15s; box-sizing: border-box; padding: 0; }
                    .km-otp-box:focus { border-color: #16a34a; background: #fff; box-shadow: 0 0 0 3px rgba(22,163,74,0.1); }
                    .km-otp-box.active { border-color: #16a34a; background: #f0fdf4; color: #15803d; }
                    .km-otp-box.otp-err { border-color: #ef4444; background: #fef2f2; }
                  `}</style>

                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>Verify Mobile Number</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280' }}>
                        OTP sent to <strong style={{ color: '#16a34a' }}>+91 {formData.phone}</strong>
                      </p>
                    </div>
                  </div>

                  {/* OTP boxes */}
                  <div className="km-otp-row" onPaste={handlePhoneOtpPaste}>
                    {phoneOtp.map((digit, idx) => (
                      <input key={idx} ref={el => phoneOtpRefs.current[idx] = el}
                        type="text" inputMode="numeric" maxLength={1} value={digit}
                        onChange={e => handlePhoneOtpChange(idx, e.target.value)}
                        onKeyDown={e => handlePhoneOtpKeyDown(idx, e)}
                        className={`km-otp-box${phoneOtpError ? ' otp-err' : digit ? ' active' : ''}`}
                      />
                    ))}
                  </div>

                  {/* Error */}
                  {phoneOtpError && (
                    <p style={{ color: '#ef4444', fontSize: '0.75rem', textAlign: 'center', margin: '-8px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <ExclamationCircleIcon style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                      {phoneOtpError}
                    </p>
                  )}

                  {/* Timer / Resend */}
                  <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                    {phoneOtpTimer > 0 ? (
                      <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                        Resend in <strong style={{ color: '#374151' }}>{phoneOtpTimer}s</strong>
                      </span>
                    ) : (
                      <button type="button" onClick={handleResendPhoneOtp} disabled={phoneOtpLoading}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#16a34a', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <ArrowPathIcon style={{ width: '13px', height: '13px' }} />
                        Resend SMS
                      </button>
                    )}
                  </div>

                  {/* Verify button */}
                  <button type="button" onClick={handleVerifyPhoneOtp}
                    disabled={phoneOtpLoading || phoneOtp.join('').length !== 6}
                    style={{ width: '100%', padding: '0.7rem', background: phoneOtpLoading || phoneOtp.join('').length !== 6 ? '#e5e7eb' : '#16a34a', color: phoneOtpLoading || phoneOtp.join('').length !== 6 ? '#9ca3af' : 'white', border: 'none', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, cursor: phoneOtpLoading || phoneOtp.join('').length !== 6 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', marginBottom: '10px', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (phoneOtp.join('').length === 6 && !phoneOtpLoading) e.currentTarget.style.background = '#15803d'; }}
                    onMouseLeave={e => { if (phoneOtp.join('').length === 6 && !phoneOtpLoading) e.currentTarget.style.background = '#16a34a'; }}
                  >
                    {phoneOtpLoading
                      ? <><ArrowPathIcon style={{ width: '16px', height: '16px', animation: 'spin 0.6s linear infinite' }} />Verifying...</>
                      : <><ShieldCheckIcon style={{ width: '16px', height: '16px' }} />Verify Mobile & Continue</>
                    }
                  </button>

                  <button type="button" onClick={() => { setStep(2); setPhoneOtp(['','','','','','']); setPhoneOtpError(''); if (phoneTimerRef.current) clearInterval(phoneTimerRef.current); }}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, margin: '0 auto' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                    <ArrowLeftIcon style={{ width: '12px', height: '12px' }} />
                    Change phone or go back
                  </button>
                </div>
              )}

              {/* ── Email OTP Section ── */}
              {step === 4 && otpSent && (
                <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>

                  {/* Icon */}
                  <div style={{ width: 52, height: 52, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <EnvelopeIcon style={{ width: '24px', height: '24px', color: '#16a34a' }} />
                  </div>

                  <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '1rem', color: '#111827' }}>Verify Your Email</p>
                  <p style={{ margin: '0 0 20px', fontSize: '0.8rem', color: '#6b7280' }}>
                    Code sent to <strong style={{ color: '#16a34a' }}>{formData.email}</strong>
                  </p>

                  {/* OTP boxes */}
                  <style>{`
                    .km-otp-wrap { display: flex; gap: 10px; justify-content: center; margin-bottom: 16px; }
                    .km-otp-input { width: 44px; height: 48px; text-align: center; font-size: 1.25rem; font-weight: 800; font-family: monospace; border: 2px solid #e5e7eb; border-radius: 10px; outline: none; color: #15803d; background: #fff; transition: all 0.15s; box-sizing: border-box; padding: 0; }
                    .km-otp-input:focus { border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
                    .km-otp-input.filled { border-color: #16a34a; background: #f0fdf4; }
                    .km-otp-input.error { border-color: #ef4444; background: #fef2f2; }
                  `}</style>

                  <div className="km-otp-wrap" onPaste={handleOtpPaste} style={{ justifyContent: 'center' }}>
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={el => otpInputRefs.current[idx] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleOtpChange(idx, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(idx, e)}
                        className={`km-otp-input${otpError ? ' error' : digit ? ' filled' : ''}`}
                      />
                    ))}
                  </div>

                  {/* Error */}
                  {otpError && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '12px' }}>
                      <ExclamationCircleIcon style={{ width: '15px', height: '15px', color: '#dc2626', flexShrink: 0 }} />
                      <span style={{ color: '#dc2626', fontSize: '0.78rem', fontWeight: 500 }}>{otpError}</span>
                    </div>
                  )}

                  {/* Timer / Resend */}
                  <div style={{ marginBottom: '16px' }}>
                    {otpTimer > 0 ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#f3f4f6', padding: '5px 12px', borderRadius: '20px' }}>
                        <ClockIcon style={{ width: '13px', height: '13px', color: '#9ca3af' }} />
                        <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Resend in <strong style={{ color: '#16a34a' }}>{otpTimer}s</strong></span>
                      </div>
                    ) : (
                      <button type="button" onClick={handleResendOtp} disabled={otpLoading}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#16a34a', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
                        Resend Code
                      </button>
                    )}
                  </div>

                  {/* Verify button */}
                  <button type="button" onClick={handleVerifyOtp}
                    disabled={otpLoading || otp.join('').length !== 6}
                    style={{ width: '100%', padding: '0.75rem', background: otpLoading || otp.join('').length !== 6 ? '#d1d5db' : 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: otpLoading || otp.join('').length !== 6 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: otpLoading || otp.join('').length !== 6 ? 'none' : '0 3px 10px rgba(22,163,74,0.25)', marginBottom: '10px' }}>
                    {otpLoading
                      ? <><ArrowPathIcon style={{ width: '17px', height: '17px', animation: 'spin 0.6s linear infinite' }} />Verifying...</>
                      : <><ShieldCheckIcon style={{ width: '17px', height: '17px' }} />Verify & Create Account</>
                    }
                  </button>

                  {/* Back */}
                  <button type="button"
                    onClick={() => { setOtpSent(false); setOtp(['','','','','','']); setOtpError(''); if (timerRef.current) clearInterval(timerRef.current); setOtpTimer(0); }}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: 0 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                    <ArrowLeftIcon style={{ width: '13px', height: '13px' }} />
                    Change email or go back
                  </button>
                </div>
              )}

            </form>
          </>
        )}
      </div>
    </div>
  );
}